'use client';

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { SCAPIShopperProductsSchemas } from 'src/types';
import { PdpWishlistButton } from '@/components/buttons/pdp-wishlist-button';
import { useEventCourseStoreInfo } from '@/providers/event-course-store-info';
import { useProductPriceWeightDisplay } from '@/components/product-utils';
import ProductPrice from '@/components/product-price-custom';
import { ExternalEventPrice } from '@/components/product-price-custom/external-event-price';
import { useProductView } from '@/providers/product-view';
import EventCourseCartButton from './event-course-cart-button';
import { getEventbriteExternalCta, isCourseProduct } from '@/lib/product-utils';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';
import ProductGiftOption from '../product-gift/product-gift-option';
import { formatEventDay, formatEventTimeRange, formatEventLocation } from './event-datetime';
import EventOccurrenceSelector from './event-occurrence-selector';
import { ShareButton } from 'src/components/buttons/share-button';
import { PrivacyConsentText } from '@/components/privacy-consent-text/privacy-consent-text';
import { usePrivacyTranslation } from '@/providers/privacy-info';

interface EventCoursePriceBoxProps {
    product: SCAPIShopperProductsSchemas['Product'];
    eventOccurrences?: SCAPIShopperProductsSchemas['Product'][];
}

/**
 * Booking box shown over the hero for event/course products: price, schedule
 * (day / time / venue), the add-to-cart action, and share/wishlist shortcuts.
 *
 * Price uses the same {@link ProductPrice} / weight-display path as standard PDPs
 * (sale, list, promo). A sold-out badge is shown when inventory exists and
 * the product is not orderable. The schedule block is only rendered when the
 * Eventbrite date/venue attributes are present.
 */
export default function EventCoursePriceBox({
    product,
    eventOccurrences = [],
}: EventCoursePriceBoxProps): ReactElement {
    const { t, i18n } = useTranslation('product');
    const { currency } = useSite();
    const { quantity } = useProductView();
    const { openDrawer } = useEventCourseStoreInfo();
    const eventCourseTerms = usePrivacyTranslation('eventCourseTerms');
    const commerceEnabled = useCommerceEnabled();
    const language = i18n.language;

    const { unitRow, perUnitLine, pricePct, isOnSale } = useProductPriceWeightDisplay(product, currency);
    const showPriceWeightUnit = Boolean(unitRow.weightText || perUnitLine);

    const isOrderable = product.inventory?.orderable ?? true;
    const seatsLabel = commerceEnabled && product.inventory && !isOrderable ? t('eventCourse.soldOut') : undefined;

    const eventDay = formatEventDay(product.c_eventStartDate, language);
    const eventTime = formatEventTimeRange(product.c_eventStartDate, product.c_eventEndDate, language);
    const eventLocation = formatEventLocation(product.c_eventVenueName, '');
    const hasDateTime = Boolean(eventDay || eventTime);
    const hasLocation = Boolean(eventLocation);
    const isCourse = isCourseProduct(product);
    const hasMultipleOccurrences = eventOccurrences.length > 1;
    const eventbriteCta = getEventbriteExternalCta(product, { commerceEnabled });
    const isFree = eventbriteCta?.reason === 'free';

    return (
        <div className="flex flex-col overflow-hidden rounded-t-xl bg-neutral-white lg:shadow-lg lg:rounded-md">
            <div className="flex flex-col gap-6 p-6 lg:p-10 lg:pt-8 lg:px-10">
                <div className="flex items-start justify-between gap-4">
                    {!commerceEnabled ? (
                        <ExternalEventPrice
                            price={product.price}
                            available={product.inventory?.orderable}
                            currency={currency}
                            className="heading-title-l mt-0"
                        />
                    ) : isFree ? (
                        <span className="heading-title-l mt-0">{t('eventCourse.freePrice')}</span>
                    ) : (
                        <ProductPrice
                            type="unit"
                            product={product}
                            quantity={quantity}
                            currency={currency}
                            labelForA11y={product?.name}
                            currentPriceProps={{
                                className: 'heading-title-l mt-0',
                            }}
                            highlightOnSaleCurrentPrice={isOnSale}
                            listPriceProps={{
                                className: 'body-copy-l-bold font-bold text-[var(--neutral-10)] line-through',
                            }}
                            promoCalloutProps={{
                                className:
                                    'label-s bg-[var(--status-red-40)] w-fit px-1 py-[1px] rounded-sm text-[var(--neutral-white)]',
                            }}
                            showPriceWeightUnit={showPriceWeightUnit}
                            pricePct={pricePct}
                            unitRow={{
                                weightText: unitRow.weightText ?? '',
                                perUnitText: perUnitLine ?? undefined,
                            }}
                        />
                    )}
                    {seatsLabel && (
                        <span className="body-copy-s-bold shrink-0 rounded-sm bg-status-green-40 px-1 py-[1px] uppercase text-neutral-white">
                            {seatsLabel}
                        </span>
                    )}
                </div>

                {hasMultipleOccurrences && (
                    <EventOccurrenceSelector occurrences={eventOccurrences} selectedProductId={product.id} />
                )}

                {hasMultipleOccurrences && hasLocation && (
                    <div className="flex items-start justify-between gap-4 rounded-md border border-neutral-80 p-4">
                        <p className="body-copy-m text-neutral-40">{eventLocation}</p>
                        <button
                            type="button"
                            onClick={openDrawer}
                            className="body-copy-m-bold shrink-0 cursor-pointer self-end underline underline-offset-2 decoration-solid">
                            {t('eventCourse.infoAndContacts')}
                        </button>
                    </div>
                )}

                {!hasMultipleOccurrences && isCourse && (hasDateTime || hasLocation) && (
                    <div className="flex flex-col gap-4">
                        {hasDateTime && (
                            <div className="rounded-md border border-neutral-80 p-4">
                                <div className="flex flex-col">
                                    {eventDay && (
                                        <p className="body-copy-m-bold text-neutral-10 capitalize">{eventDay}</p>
                                    )}
                                    {eventTime && <p className="body-copy-m text-neutral-40">{eventTime}</p>}
                                </div>
                            </div>
                        )}

                        {hasLocation && (
                            <div className="flex items-start justify-between gap-4 rounded-md border border-neutral-80 p-4">
                                <p className="body-copy-m text-neutral-40">{eventLocation}</p>
                                <button
                                    type="button"
                                    onClick={openDrawer}
                                    className="body-copy-m-bold shrink-0 cursor-pointer self-end underline underline-offset-2 decoration-solid">
                                    {t('eventCourse.infoAndContacts')}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {!hasMultipleOccurrences && !isCourse && (hasDateTime || hasLocation) && (
                    <div className="flex items-start justify-between gap-4 rounded-md border border-neutral-80 p-4">
                        <div className="flex flex-col">
                            {eventDay && <p className="body-copy-m-bold text-neutral-10 capitalize">{eventDay}</p>}
                            {eventTime && <p className="body-copy-m text-neutral-40">{eventTime}</p>}
                            {eventLocation && <p className="body-copy-m text-neutral-40">{eventLocation}</p>}
                        </div>
                        <button
                            type="button"
                            onClick={openDrawer}
                            className="body-copy-m-bold shrink-0 cursor-pointer self-end underline underline-offset-2 decoration-solid">
                            {t('eventCourse.infoAndContacts')}
                        </button>
                    </div>
                )}

                {!eventbriteCta && <ProductGiftOption product={product} />}

                <div className="flex items-center gap-3">
                    <EventCourseCartButton />
                    <div className="w-[52px] min-w-[52px]">
                        <ShareButton product={product} size="lg" />
                    </div>
                    <div className="w-[52px] min-w-[52px]">
                        <PdpWishlistButton product={product} size="lg" hierarchy="secondary" />
                    </div>
                </div>
            </div>

            {eventCourseTerms && (
                <div className="flex items-center justify-center bg-[var(--brown-20)] px-4 py-3 text-neutral-white">
                    <PrivacyConsentText
                        html={eventCourseTerms}
                        className="body-copy-s text-center"
                        linkClassName="underline underline-offset-2"
                    />
                </div>
            )}
        </div>
    );
}
